import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ontruck/orc';
import { STOP_STATES, STOP_ACTIONS } from 'utils/constants/jobDetails';
import { put } from 'utils/network/http';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import ButtonLoading from 'components/ButtonLoading';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import { usePendingPods } from 'hooks/usePendingPods/usePendingPods';

const checkPreviousState = (index, shipment_status) =>
  index > 0 ? !!shipment_status[index - 1].id : true;

const StopState = ({
  state,
  index,
  handleAdvanceJourney,
  full_status,
  loading,
  stop_id,
  showAdvanceButton,
  is_late,
  shipment_status,
  journeyStatus
}) => {
  const { t } = useTranslation('Jobs');
  if (!STOP_STATES[state.status]) return null;
  const shouldShowButton =
    stop_id === full_status.advance_payload?.stop_id &&
    STOP_ACTIONS[`${state.status}_ACTION`] &&
    !state.id &&
    checkPreviousState(index, shipment_status);

  return (
    <div
      className="stop-states__item stop-states__item--transit"
      data-testid="stop-state"
    >
      <div
        className={`stop-states__icon-container ${
          state.id ? 'stop-states__icon-container--completed' : ''
        } ${
          shouldShowButton ? 'stop-states__icon-container--withAction' : ''
        } ${
          shouldShowButton && is_late
            ? 'stop-states__icon-container--is-late'
            : ''
        }`}
      >
        <div
          className={`stop-states__icon ${
            state.id ? 'stop-states__icon--completed' : ''
          }`}
        >
          {state.id && <Icon glyph="check" size=".8rem" />}
        </div>
      </div>
      <div className="stop-states__body">
        <div className="stop-states__title">{t(STOP_STATES[state.status])}</div>
        {state.created_at && (
          <div className="stop-states__date-time">
            {moment(state.created_at).format('D MMM - HH:mm')}
          </div>
        )}
        {shouldShowButton && is_late && (
          <div
            className="stop-states__late-label"
            data-testid="stop-states__late-label"
          >
            {t('isLate')}
          </div>
        )}
        {state.status === STOP_STATES.DELIVERED &&
          state.id &&
          !shipment_status[index + 1].id && (
            <div
              className="stop-states__pending-label"
              data-testid="stop-states__pending-label"
            >
              {t('doneWithoutManifest')}
            </div>
          )}
        {state.status === STOP_STATES.DELIVERED &&
          state.id &&
          shipment_status[index + 1].id &&
          journeyStatus === 'DONE_WITHOUT_MANIFEST' && (
            <div
              className="stop-states__reject-label"
              data-testid="stop-states__reject-label"
            >
              {t('rejectedManifest')}
            </div>
          )}
        {shouldShowButton && showAdvanceButton && (
          <ButtonLoading
            testId="stop-states__action"
            loading={loading}
            handleClick={handleAdvanceJourney}
            className="stop-states__action"
            text={t(STOP_ACTIONS[`${state.status}_ACTION`])}
          />
        )}
      </div>
    </div>
  );
};

const StopStates = ({
  className,
  shipment_status,
  full_status,
  stop_id,
  showAdvanceButton,
  is_late,
  refetchData,
  journeyStatus,
  journeyId
}) => {
  const [loading, setLoading] = useState(false);
  const { refetchPendingPods } = usePendingPods();

  const currentStatus = useMemo(() => {
    return shipment_status.reduce(
      (status, ss) => (ss.id ? ss.status : status),
      ''
    );
  }, [shipment_status]);

  const handleAdvanceJourney = useCallback(async () => {
    try {
      setLoading(true);
      await put(full_status.advance_url, full_status.advance_payload);
      sendEventToSegment(TRACKING_EVENTS.JOURNEY_STATUS_ADVANCED, {
        current_status: currentStatus,
        next_status: full_status.next,
        journeyId
      });
      await refetchData();
      await refetchPendingPods();
    } catch (e) {
      console.error('Error putAdvanceJourney ', e);
    } finally {
      setLoading(false);
    }
  }, [full_status, refetchData, currentStatus, refetchPendingPods, journeyId]);

  return (
    <div className={className} data-testid="stop-states">
      <div className="stop-states">
        {shipment_status.map((state, index) => (
          <StopState
            key={`${state.status}-${index}`}
            state={state}
            index={index}
            handleAdvanceJourney={handleAdvanceJourney}
            full_status={full_status}
            loading={loading}
            stop_id={stop_id}
            showAdvanceButton={showAdvanceButton}
            is_late={is_late}
            shipment_status={shipment_status}
            journeyStatus={journeyStatus}
          />
        ))}
      </div>
    </div>
  );
};

StopStates.propTypes = {
  className: PropTypes.string,
  shipment_status: PropTypes.array.isRequired,
  full_status: PropTypes.object.isRequired,
  stop_id: PropTypes.number.isRequired,
  showAdvanceButton: PropTypes.bool.isRequired,
  is_late: PropTypes.bool.isRequired,
  refetchData: PropTypes.func.isRequired,
  journeyStatus: PropTypes.string.isRequired,
  journeyId: PropTypes.string.isRequired
};

export default StopStates;
